import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { getUserDetails, getSessionToken, deleteAccount, clearSessionToken, logout } from '../Services/services';
import { useBranding } from '../Context/BrandingContext';
import { ROLES, hasRole } from '../utils/roles';
import AppHeader from './AppHeader';

function Account() {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { branding } = useBranding();

  const userRole = userDetails?.role || auth.role || ROLES.PENDING;
  const isPending = userRole === ROLES.PENDING || userRole === ROLES.GUEST;
  const isStandard = hasRole(userRole, ROLES.STANDARD);
  const isSupport = hasRole(userRole, ROLES.SUPPORT);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!getSessionToken()) {
      navigate('/login?redirect=/account', { replace: true });
      return;
    }

    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetails();
        setUserDetails(response.data);
      } catch (err) {
        if (err.response?.status === 401) {
          navigate('/login?redirect=/account', { replace: true });
        } else {
          setError('Failed to load user details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [navigate]);

  const handleLogout = async () => {
    try { await logout(); } catch (err) { /* ignore */ }
    clearSessionToken();
    navigate('/', { replace: true });
  };

  const handleDeleteAccount = async () => {
    setDeleteInProgress(true);
    setDeleteError('');
    try {
      await deleteAccount();
      clearSessionToken();
      dispatch({ type: 'CLEAR_AUTH' });
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError('Something went wrong. Please try again or contact support.');
      setDeleteInProgress(false);
    }
  };

  if (loading) {
    const spinnerStyle = {
      ...styles.spinner,
      borderTop: `4px solid ${branding.primaryColor}`,
    };
    return (
      <div style={styles.loadingContainer}>
        <div style={spinnerStyle}></div>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  // Dynamic styles based on branding
  const dynamicStyles = {
    featureTitle: {
      ...styles.featureTitle,
      color: branding.primaryColor,
    },
  };

  return (
    <div style={styles.page}>
      <AppHeader />

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h1 style={styles.title}>My Account</h1>
          </div>

          {/* Pending notice — shown immediately below title */}
          {isPending && (
            <div style={styles.pendingNotice}>
              <h3 style={styles.pendingTitle}>Account Pending Approval</h3>
              <p style={styles.pendingText}>Your account is awaiting approval. Once approved, you'll have access to additional features.</p>
            </div>
          )}

          {/* API errors (not shown for guests — they get 403 by design) */}
          {error && !isPending && <div style={styles.error}>{error}</div>}

          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>User Information</h2>
            <div style={styles.infoRow}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{userDetails?.email || auth.email || ''}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Full Name:</span>
              <span style={styles.value}>{userDetails?.full_name || auth.fullName || ''}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Company:</span>
              <span style={styles.value}>{userDetails?.company || auth.company || ''}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Phone:</span>
              <span style={styles.value}>{userDetails?.phone || auth.phone || ''}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Job Title:</span>
              <span style={styles.value}>{userDetails?.job_title || auth.jobTitle || ''}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Part Sales:</span>
              <span style={styles.value}>{(userDetails?.part_sales ?? auth.partSales) ? 'Yes' : 'No'}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>MRO:</span>
              <span style={styles.value}>{(userDetails?.mro ?? auth.mro) ? 'Yes' : 'No'}</span>
            </div>
          </div>


          {/* Support-only features */}
          {isSupport && (
            <div style={styles.section}>
              <h2 style={styles.sectionTitle}>Support Features</h2>
              <div style={styles.featureGrid}>
                <div style={styles.featureCard}>
                  <h3 style={dynamicStyles.featureTitle}>Case Management</h3>
                  <p style={styles.featureDesc}>Create and manage support cases</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={dynamicStyles.featureTitle}>Change Requests</h3>
                  <p style={styles.featureDesc}>Submit change requests</p>
                </div>
                <div style={styles.featureCard}>
                  <h3 style={dynamicStyles.featureTitle}>License Management</h3>
                  <p style={styles.featureDesc}>View and manage licenses</p>
                </div>
              </div>
            </div>
          )}

          {/* Sign out */}
          <div style={styles.signOutSection}>
            <button type="button" style={{ ...styles.signOutButton, color: branding.primaryColor, borderColor: branding.primaryColor }} onClick={handleLogout}>
              Sign out
            </button>
          </div>

          {/* GDPR — Data deletion */}
          <div style={styles.deleteSection}>
            {!showDeleteConfirm ? (
              <button
                style={styles.deleteButton}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete my account and related data
              </button>
            ) : (
              <div style={styles.confirmBox}>
                <h3 style={styles.confirmTitle}>Are you sure?</h3>
                <p style={styles.confirmText}>
                  Your portal account and any pending cart items will be permanently removed.
                  Your company's transaction history and any orders placed on your behalf will remain
                  intact — only your access record will be deleted. This action cannot be undone.
                </p>
                {deleteError && <p style={styles.deleteErrorText}>{deleteError}</p>}
                <div style={styles.confirmActions}>
                  <button
                    style={styles.cancelDeleteButton}
                    onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); }}
                    disabled={deleteInProgress}
                  >
                    Cancel
                  </button>
                  <button
                    style={styles.confirmDeleteButton}
                    onClick={handleDeleteAccount}
                    disabled={deleteInProgress}
                  >
                    {deleteInProgress ? 'Deleting…' : 'I understand, delete my account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer style={styles.footer}>
        <span style={styles.footerText}>
          © {new Date().getFullYear()} {branding.companyName}. All rights reserved. · <Link to="/health" style={styles.footerLink}>System Status</Link>
        </span>
      </footer>
    </div>
  );
}

const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  main: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    backgroundColor: '#f0f4f8',
    padding: '40px 24px',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    width: '100%',
    maxWidth: '600px',
  },
  cardHeader: {
    textAlign: 'center',
    marginBottom: '24px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e2e8f0',
  },
  title: {
    margin: '0',
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a2e',
  },
  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #eee',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#1a1a2e',
  },
  infoRow: {
    display: 'flex',
    marginBottom: '12px',
  },
  label: {
    fontWeight: '600',
    width: '100px',
    color: '#666',
  },
  value: {
    flex: 1,
    color: '#333',
  },
  error: {
    color: '#d32f2f',
    fontSize: '14px',
    marginBottom: '16px',
    padding: '12px',
    backgroundColor: '#ffebee',
    borderRadius: '4px',
  },
  backButton: {
    marginTop: '20px',
    padding: '12px 24px',
    backgroundColor: 'white',
    border: '2px solid #0066cc',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  pendingNotice: {
    backgroundColor: '#fff3cd',
    border: '1px solid #ffc107',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '24px',
  },
  pendingTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#856404',
  },
  pendingText: {
    margin: 0,
    color: '#856404',
    fontSize: '14px',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  featureCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid #e9ecef',
  },
  featureTitle: {
    margin: '0 0 8px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#0066cc',
  },
  featureDesc: {
    margin: 0,
    fontSize: '14px',
    color: '#666',
  },
  footer: {
    backgroundColor: 'white',
    padding: '20px 24px',
    textAlign: 'center',
    borderTop: '1px solid #e2e8f0',
  },
  footerText: {
    fontSize: '13px',
    color: '#718096',
  },
  footerLink: {
    color: '#718096',
    textDecoration: 'none',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #0066cc',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    color: '#666',
    fontSize: '16px',
  },
  signOutSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #eee',
  },
  signOutButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#ffffff',
    padding: '8px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    border: '1.5px solid',
    cursor: 'pointer',
  },
  deleteSection: {
    marginTop: '8px',
    paddingTop: '24px',
    borderTop: '1px solid #eee',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  deleteButton: {
    background: 'none',
    border: 'none',
    color: '#b91c1c',
    fontSize: '14px',
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
  },
  confirmBox: {
    backgroundColor: '#fff5f5',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '20px',
    width: '100%',
  },
  confirmTitle: {
    margin: '0 0 10px 0',
    fontSize: '16px',
    fontWeight: '700',
    color: '#7f1d1d',
  },
  confirmText: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#7f1d1d',
    lineHeight: '1.5',
  },
  confirmActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  cancelDeleteButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  confirmDeleteButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#b91c1c',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  deleteErrorText: {
    color: '#b91c1c',
    fontSize: '13px',
    marginBottom: '12px',
  },
};

export default Account;
